import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Loader2, Sparkles, Clock, Users, ChevronDown, ChevronUp } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_LABEL = {
  author: "Author",
  beta_reader: "Beta Reader",
  both: "Both",
};

export default function AdminPrivateTesterRequests() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [state, setState] = useState({ loading: true, pending: [], decided: [] });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/private-tester-requests");
      setState({ loading: false, pending: data.pending || [], decided: data.decided || [] });
    } catch (e) {
      toast.error(formatApiError(e));
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    else setState({ loading: false, pending: [], decided: [] });
  }, [isAdmin]);

  const approve = async (row) => {
    if (!window.confirm(`Approve ${row.user?.display_name || row.user?.email}? They will immediately gain Private Tester access and be notified.`)) return;
    setBusyId(row.id);
    try {
      await api.post(`/admin/private-tester-requests/${row.id}/approve`);
      toast.success("Approved. The user has been notified.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const decline = async (row) => {
    const note = window.prompt("Optional note to the requester (leave blank to send a default message):", "");
    if (note === null) return; // cancelled
    setBusyId(row.id);
    try {
      await api.post(`/admin/private-tester-requests/${row.id}/decline`, { note: note || null });
      toast.success("Request declined. The user has been notified.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!isAdmin) {
    return (
      <SiteShell>
        <section className="container-page py-16">
          <p className="text-muted-foreground">Admins only.</p>
        </section>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section className="container-page py-10 md:py-14 pb-32 md:pb-24" data-testid="admin-private-tester-requests-root">
        <span className="chip"><Sparkles size={14} /> Admin · Private Testers</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">Private Tester Requests</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Members who have asked for real-project access while Last Bookmark
          is in beta. Approving flips their existing account into a Private
          Tester — no separate role or system is created — and notifies them
          in-app (plus by email if they've opted in).
        </p>

        <h2 className="mt-8 font-serif text-2xl flex items-center gap-2">
          <Clock size={20} className="text-[hsl(var(--sb-gold-2))]" /> Pending
          <span className="text-sm font-normal text-muted-foreground">({state.pending.length})</span>
        </h2>
        {state.loading ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
        ) : state.pending.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid="admin-pt-empty">
            No pending requests right now.
          </p>
        ) : (
          <ul className="mt-3 space-y-3" data-testid="admin-pt-pending-list">
            {state.pending.map((row) => (
              <RequestRow
                key={row.id}
                row={row}
                busy={busyId === row.id}
                onApprove={() => approve(row)}
                onDecline={() => decline(row)}
              />
            ))}
          </ul>
        )}

        <h2 className="mt-10 font-serif text-2xl flex items-center gap-2">
          <Users size={20} className="text-[hsl(var(--sb-forest-2))]" /> History
          <span className="text-sm font-normal text-muted-foreground">({state.decided.length})</span>
        </h2>
        {state.decided.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Decisions you make will appear here.</p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="admin-pt-history-list">
            {state.decided.map((row) => (
              <HistoryRow key={row.id} row={row} />
            ))}
          </ul>
        )}
      </section>
    </SiteShell>
  );
}


function HistoryRow({ row }) {
  const [open, setOpen] = useState(false);
  const hasNote = !!(row.decision_note && row.decision_note.trim());
  const decidedLabel = row.decided_at ? new Date(row.decided_at).toLocaleString() : "";
  return (
    <li
      className="rounded-2xl border bg-card"
      data-testid={`admin-pt-history-row-${row.id}`}
    >
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <UserBlock user={row.user} />
        <span className="text-xs text-muted-foreground shrink-0">
          Wants to test as <strong>{ROLE_LABEL[row.role_preference] || row.role_preference}</strong>
        </span>
        <StatusPill status={row.status} />
        <span className="text-xs text-muted-foreground ml-auto">{decidedLabel}</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-muted/40 transition-colors"
          aria-expanded={open}
          aria-controls={`admin-pt-history-details-${row.id}`}
          data-testid={`admin-pt-history-toggle-${row.id}`}
        >
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {open ? "Hide decision details" : "View decision details"}
        </button>
      </div>
      {open && (
        <div
          id={`admin-pt-history-details-${row.id}`}
          className="border-t px-4 py-3 bg-muted/30 rounded-b-2xl text-xs"
          data-testid={`admin-pt-history-details-${row.id}`}
        >
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]">
            <dt className="text-muted-foreground">Decision</dt>
            <dd className="text-foreground/90">
              {row.status === "approved" ? "Approved" : row.status === "declined" ? "Declined" : row.status}
            </dd>
            <dt className="text-muted-foreground">When</dt>
            <dd className="text-foreground/90">{decidedLabel || "—"}</dd>
            <dt className="text-muted-foreground">Note to requester</dt>
            <dd
              className="text-foreground/90"
              data-testid={`admin-pt-history-note-${row.id}`}
            >
              {hasNote ? (
                <span className="italic whitespace-pre-wrap">&ldquo;{row.decision_note}&rdquo;</span>
              ) : (
                <span className="text-muted-foreground">Default message sent</span>
              )}
            </dd>
          </dl>
          <p className="mt-3 text-[10.5px] text-muted-foreground">
            Admin-only. Never shown on public profiles or to other users.
          </p>
        </div>
      )}
    </li>
  );
}


function RequestRow({ row, busy, onApprove, onDecline }) {
  return (
    <li
      className="rounded-2xl border bg-card px-4 py-4 md:px-5 md:py-4 flex flex-col md:flex-row gap-3 md:items-center"
      data-testid={`admin-pt-row-${row.id}`}
    >
      <UserBlock user={row.user} />
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          Wants to test as <strong className="text-foreground">{ROLE_LABEL[row.role_preference] || row.role_preference}</strong>
        </div>
        {row.note && (
          <p className="mt-1 text-xs text-muted-foreground italic whitespace-pre-wrap">"{row.note}"</p>
        )}
        <div className="mt-1 text-[11px] text-muted-foreground">
          Submitted {row.created_at ? new Date(row.created_at).toLocaleString() : ""}
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          className="btn-primary"
          onClick={onApprove}
          disabled={busy}
          data-testid={`admin-pt-approve-${row.id}`}
        >
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Approve
        </button>
        <button
          type="button"
          className="btn-ghost border text-destructive"
          onClick={onDecline}
          disabled={busy}
          data-testid={`admin-pt-decline-${row.id}`}
        >
          <X size={14} /> Decline
        </button>
      </div>
    </li>
  );
}


function UserBlock({ user }) {
  if (!user) return <span className="text-sm text-muted-foreground">Unknown user</span>;

  const initial = (user.display_name || user.email || "?")[0]?.toUpperCase();
  const inner = (
    <>
      {user.avatar_url ? (
        <img
          src={user.avatar_url}
          alt=""
          className="w-10 h-10 rounded-full object-cover shrink-0 ring-1 ring-transparent group-hover:ring-[hsl(var(--sb-forest))]/60 transition-shadow"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))] font-semibold shrink-0 ring-1 ring-transparent group-hover:ring-[hsl(var(--sb-forest))]/60 transition-shadow">
          {initial}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-sm font-medium truncate group-hover:underline underline-offset-4 decoration-[hsl(var(--sb-forest))]/50 decoration-1">
          {user.display_name || user.email}
        </div>
        <div className="text-xs text-muted-foreground truncate">{user.email}</div>
      </div>
    </>
  );

  // Applicant name + avatar link to the same public profile page the rest
  // of the app uses (``/profile/:userId`` → ``PublicProfilePage``). Admins
  // can view any user's profile via ``GET /api/users/{id}`` even when the
  // user is not yet Early Access, so nothing else needs to change.
  if (user.id) {
    return (
      <Link
        to={`/profile/${user.id}`}
        className="group flex items-center gap-3 min-w-0 rounded-full pr-2 -ml-1 pl-1 py-1 hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-forest))]/40 transition-colors"
        aria-label={`View profile for ${user.display_name || user.email}`}
        data-testid={`admin-pt-user-link-${user.id}`}
      >
        {inner}
      </Link>
    );
  }
  return <div className="flex items-center gap-3 min-w-0">{inner}</div>;
}


function StatusPill({ status }) {
  if (status === "approved")
    return <span className="chip-success" data-testid="admin-pt-status-approved">Approved</span>;
  if (status === "declined")
    return <span className="chip" data-testid="admin-pt-status-declined">Declined</span>;
  if (status === "superseded")
    return <span className="chip" data-testid="admin-pt-status-superseded">Superseded</span>;
  return <span className="chip">{status}</span>;
}
